/**
 * Restaurant form management including fast fill and magic import.
 */

import { i18n, getLanguage } from './i18n';
import { ApiService, escapeHtml, getPreferredCountryCode, getPlacesLanguageCode, getStoredLocation } from './services';
import { Restaurant, DuplicateCheckResponse } from './types';

export class RestaurantFormManager {
    private form: HTMLFormElement;
    private formContainer: HTMLElement;
    private cancelBtn: HTMLButtonElement;
    private fastFillAutocomplete: HTMLElement;
    private fastFillRecognized: HTMLElement;
    private fastFillAutocompleteZh: HTMLElement;
    private fastFillRecognizedZh: HTMLElement;

    private api: ApiService;
    private editingRestaurantId: string | null = null;
    private duplicateRestaurant: Restaurant | null = null;
    private selectedFastFillPlaceId: string | null = null;
    private activeNameInput: 'en' | 'zh' = 'en';  // Track which name field triggered autocomplete

    private fastFillAutocompleteTimer: number | null = null;
    private fastFillAutocompleteRequestId: number = 0;
    private addressCheckTimer: number | null = null;

    // Tag input state
    private availableTags: string[] = [];
    private selectedTags: string[] = [];
    private tagInputElement: HTMLInputElement | null = null;
    private tagChipsContainer: HTMLElement | null = null;
    private tagSuggestionsContainer: HTMLElement | null = null;
    private selectedTagSuggestionIndex: number = -1;

    private onFormSubmit: (isEdit: boolean, restaurantId: string | null) => Promise<void>;
    private onFormHide: () => void;

    constructor(
        form: HTMLFormElement,
        formContainer: HTMLElement,
        cancelBtn: HTMLButtonElement,
        fastFillAutocomplete: HTMLElement,
        fastFillRecognized: HTMLElement,
        api: ApiService,
        onFormSubmit: (isEdit: boolean, restaurantId: string | null) => Promise<void>,
        onFormHide: () => void
    ) {
        this.form = form;
        this.formContainer = formContainer;
        this.cancelBtn = cancelBtn;
        this.fastFillAutocomplete = fastFillAutocomplete;
        this.fastFillRecognized = fastFillRecognized;
        // Get Chinese name field autocomplete elements
        this.fastFillAutocompleteZh = document.getElementById('fastFillAutocompleteZh') as HTMLElement;
        this.fastFillRecognizedZh = document.getElementById('fastFillRecognizedZh') as HTMLElement;
        this.api = api;
        this.onFormSubmit = onFormSubmit;
        this.onFormHide = onFormHide;

        this.setupEventListeners();
    }

    private setupEventListeners(): void {
        this.cancelBtn.addEventListener('click', () => this.hide());
        this.form.addEventListener('submit', (e) => this.handleSubmit(e));

        // Fast fill button and autocomplete
        const fastFillBtn = document.getElementById('fastFillBtn');
        if (fastFillBtn) {
            fastFillBtn.addEventListener('click', () => this.handleFastFill());
        }

        // English name input with autocomplete
        const nameEnInput = document.getElementById('nameEn') as HTMLInputElement;
        if (nameEnInput) {
            nameEnInput.addEventListener('input', () => {
                this.activeNameInput = 'en';
                this.scheduleFastFillAutocomplete();
            });
            nameEnInput.addEventListener('blur', () => {
                setTimeout(() => this.fastFillAutocomplete.classList.add('hidden'), 200);
            });
            nameEnInput.addEventListener('focus', () => {
                this.activeNameInput = 'en';
                if (nameEnInput.value.trim().length >= 2) {
                    this.scheduleFastFillAutocomplete();
                }
            });
        }

        // Chinese name input with autocomplete (same functionality)
        const nameZhInput = document.getElementById('nameZh') as HTMLInputElement;
        if (nameZhInput) {
            nameZhInput.addEventListener('input', () => {
                this.activeNameInput = 'zh';
                this.scheduleFastFillAutocomplete();
            });
            nameZhInput.addEventListener('blur', () => {
                setTimeout(() => {
                    if (this.fastFillAutocompleteZh) {
                        this.fastFillAutocompleteZh.classList.add('hidden');
                    }
                }, 200);
            });
            nameZhInput.addEventListener('focus', () => {
                this.activeNameInput = 'zh';
                if (nameZhInput.value.trim().length >= 2) {
                    this.scheduleFastFillAutocomplete();
                }
            });
        }

        // Address and place_id duplicate check
        const addressInput = document.getElementById('address') as HTMLInputElement;
        if (addressInput) {
            addressInput.addEventListener('input', () => this.scheduleAddressDuplicateCheck());
            addressInput.addEventListener('blur', () => this.scheduleAddressDuplicateCheck());
        }

        const placeIdInput = document.getElementById('placeId') as HTMLInputElement;
        if (placeIdInput) {
            placeIdInput.addEventListener('input', () => this.scheduleAddressDuplicateCheck());
            placeIdInput.addEventListener('blur', () => this.scheduleAddressDuplicateCheck());
        }

        // Tag input setup
        this.tagInputElement = document.getElementById('tagInput') as HTMLInputElement;
        this.tagChipsContainer = document.getElementById('tagChips');
        this.tagSuggestionsContainer = document.getElementById('tagSuggestions');
        this.setupTagInputListeners();
    }

    show(restaurant?: Restaurant): void {
        this.formContainer.classList.remove('hidden');
        this.form.reset();
        const formTitle = document.getElementById('formTitle');
        const submitBtn = document.getElementById('submitFormBtn');

        // Reset tag input state
        this.selectedTags = [];
        this.renderTagChips();
        if (this.tagInputElement) {
            this.tagInputElement.value = '';
        }
        this.hideTagSuggestions();

        if (restaurant) {
            // Edit mode
            this.editingRestaurantId = restaurant.id;
            (document.getElementById('nameEn') as HTMLInputElement).value = restaurant.name_en;
            (document.getElementById('nameZh') as HTMLInputElement).value = restaurant.name_zh !== restaurant.name_en ? restaurant.name_zh : '';
            (document.getElementById('cuisine') as HTMLInputElement).value = restaurant.cuisine;
            (document.getElementById('address') as HTMLInputElement).value = restaurant.address;
            (document.getElementById('summary') as HTMLTextAreaElement).value = restaurant.summary;

            // Populate Magic Fill fields if available
            if (restaurant.place_id) {
                (document.getElementById('placeId') as HTMLInputElement).value = restaurant.place_id;
            }
            if (restaurant.price_level) {
                (document.getElementById('priceLevel') as HTMLSelectElement).value = String(restaurant.price_level);
            }
            if (restaurant.budget_category) {
                (document.getElementById('budgetCategory') as HTMLSelectElement).value = restaurant.budget_category;
            }
            if (restaurant.top_dishes && restaurant.top_dishes.length > 0) {
                (document.getElementById('topDishes') as HTMLInputElement).value = restaurant.top_dishes.join(', ');
            }
            if (restaurant.keywords && restaurant.keywords.length > 0) {
                (document.getElementById('keywords') as HTMLInputElement).value = restaurant.keywords.join(', ');
            }
            if (restaurant.tags && restaurant.tags.length > 0) {
                this.selectedTags = [...restaurant.tags];
                this.renderTagChips();
            }
            if (restaurant.opening_hours) {
                (document.getElementById('openingHours') as HTMLTextAreaElement).value = restaurant.opening_hours;
            }

            if (formTitle) formTitle.textContent = i18n.editRestaurant;
            if (submitBtn) submitBtn.textContent = i18n.save;
        } else {
            // Add mode
            this.editingRestaurantId = null;
            (document.getElementById('placeId') as HTMLInputElement).value = '';
            if (formTitle) formTitle.textContent = i18n.formTitle;
            if (submitBtn) submitBtn.textContent = i18n.submit;
        }
    }

    hide(): void {
        this.formContainer.classList.add('hidden');
        this.editingRestaurantId = null;
        this.duplicateRestaurant = null;
        this.clearDuplicateWarning();
        this.onFormHide();
    }

    isEditing(): boolean {
        return this.editingRestaurantId !== null;
    }

    private async handleSubmit(e: Event): Promise<void> {
        e.preventDefault();

        let nameEn = (document.getElementById('nameEn') as HTMLInputElement).value.trim();
        let nameZh = (document.getElementById('nameZh') as HTMLInputElement).value.trim();

        // Validate that at least one name is provided
        if (!nameEn && !nameZh) {
            alert(i18n.pleaseEnterName);
            return;
        }

        // If one name is missing, set it to match the other
        if (!nameEn) {
            nameEn = nameZh;
        }
        if (!nameZh) {
            nameZh = nameEn;
        }

        const cuisine = (document.getElementById('cuisine') as HTMLInputElement).value;
        const address = (document.getElementById('address') as HTMLInputElement).value;
        const summary = (document.getElementById('summary') as HTMLTextAreaElement).value;

        const priceLevelSelect = document.getElementById('priceLevel') as HTMLSelectElement;
        const priceLevelValue = priceLevelSelect.value;
        const priceLevel = priceLevelValue ? parseInt(priceLevelValue, 10) : null;

        const budgetCategory = (document.getElementById('budgetCategory') as HTMLSelectElement).value;

        const topDishesInput = (document.getElementById('topDishes') as HTMLInputElement).value;
        const topDishes = topDishesInput.trim()
            ? topDishesInput.split(',').map(d => d.trim()).filter(d => d)
            : [];

        const keywordsInput = (document.getElementById('keywords') as HTMLInputElement).value;
        const keywords = keywordsInput.trim()
            ? keywordsInput.split(',').map(k => k.trim()).filter(k => k)
            : [];

        // Tags are managed via chip UI, use selectedTags state
        const tags = [...this.selectedTags];

        const openingHours = (document.getElementById('openingHours') as HTMLTextAreaElement).value;
        const placeId = (document.getElementById('placeId') as HTMLInputElement).value.trim();

        const payload = {
            name_en: nameEn,
            name_zh: nameZh,
            cuisine: cuisine,
            address: address,
            summary: summary,
            place_id: placeId,
            price_level: priceLevel,
            budget_category: budgetCategory,
            top_dishes: topDishes,
            keywords: keywords,
            tags: tags,
            opening_hours: openingHours
        };

        try {
            const editId = this.editingRestaurantId ?? this.duplicateRestaurant?.id ?? null;
            const isEdit = editId !== null;
            const url = isEdit ? `api/restaurants/${editId}` : 'api/restaurants';
            const method = isEdit ? 'PUT' : 'POST';

            await this.api.fetch<void>(url, {
                method: method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            this.hide();
            await this.onFormSubmit(isEdit, editId);
        } catch (error) {
            console.error('Error saving restaurant:', error);
        }
    }

    // Duplicate detection
    private scheduleAddressDuplicateCheck(): void {
        if (this.editingRestaurantId !== null) return;

        if (this.addressCheckTimer) {
            clearTimeout(this.addressCheckTimer);
        }

        this.addressCheckTimer = window.setTimeout(() => {
            this.checkAddressDuplicate();
        }, 500);
    }

    async checkAddressDuplicate(): Promise<void> {
        const addressInput = document.getElementById('address') as HTMLInputElement;
        const placeIdInput = document.getElementById('placeId') as HTMLInputElement;
        const address = addressInput?.value.trim();
        const placeId = placeIdInput?.value.trim();

        if (!address && !placeId) {
            this.clearDuplicateWarning();
            this.duplicateRestaurant = null;
            return;
        }

        try {
            const data = await this.api.fetch<DuplicateCheckResponse>('api/restaurants/check-duplicate', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    address: address || '',
                    place_id: placeId || ''
                })
            });

            if (data.is_duplicate && data.existing_restaurant) {
                this.duplicateRestaurant = data.existing_restaurant;
                this.showDuplicateWarning(data.existing_restaurant, data.matched_by);
            } else {
                this.duplicateRestaurant = null;
                this.clearDuplicateWarning();
            }
        } catch (error) {
            console.error('Error checking for duplicate:', error);
        }
    }

    private showDuplicateWarning(restaurant: Restaurant, matchedBy: 'place_id' | 'address' | null): void {
        const addressInput = document.getElementById('address') as HTMLInputElement;
        const placeIdInput = document.getElementById('placeId') as HTMLInputElement;
        const submitBtn = document.getElementById('submitFormBtn') as HTMLButtonElement;
        const formTitle = document.getElementById('formTitle');

        const targetInput = matchedBy === 'place_id' ? placeIdInput : addressInput;
        targetInput?.classList.add('duplicate-warning');

        let warningEl = document.getElementById('duplicateWarning');
        if (!warningEl) {
            warningEl = document.createElement('div');
            warningEl.id = 'duplicateWarning';
            warningEl.className = 'duplicate-warning-message';
        }
        targetInput?.parentNode?.insertBefore(warningEl, targetInput.nextSibling);

        const matchFieldText = matchedBy === 'place_id' ? i18n.googlePlaceId : i18n.addressLabel;

        const warningText = getLanguage() === 'zh'
            ? `⚠️ 此${matchFieldText}已存在餐厅: "${restaurant.name_en}"。点击保存将更新现有记录。`
            : `⚠️ A restaurant already exists with this ${matchFieldText}: "${restaurant.name_en}". Clicking save will update the existing record.`;
        warningEl.textContent = warningText;
        warningEl.classList.remove('hidden');

        if (submitBtn) {
            submitBtn.textContent = i18n.updateRestaurant;
            submitBtn.classList.add('btn-warning');
        }

        if (formTitle) {
            formTitle.textContent = i18n.updateExisting;
        }
    }

    private clearDuplicateWarning(): void {
        const addressInput = document.getElementById('address') as HTMLInputElement;
        const placeIdInput = document.getElementById('placeId') as HTMLInputElement;
        const submitBtn = document.getElementById('submitFormBtn') as HTMLButtonElement;
        const formTitle = document.getElementById('formTitle');
        const warningEl = document.getElementById('duplicateWarning');

        addressInput?.classList.remove('duplicate-warning');
        placeIdInput?.classList.remove('duplicate-warning');

        if (warningEl) {
            warningEl.classList.add('hidden');
        }

        if (submitBtn && this.editingRestaurantId === null) {
            submitBtn.textContent = i18n.submit;
            submitBtn.classList.remove('btn-warning');
        }

        if (formTitle && this.editingRestaurantId === null) {
            formTitle.textContent = i18n.formTitle;
        }
    }

    // Fast fill autocomplete
    private scheduleFastFillAutocomplete(): void {
        if (this.fastFillAutocompleteTimer !== null) {
            clearTimeout(this.fastFillAutocompleteTimer);
        }

        this.fastFillAutocompleteTimer = window.setTimeout(() => {
            this.handleFastFillAutocomplete();
        }, 300);
    }

    private getActiveAutocompleteElements(): { input: HTMLInputElement; autocomplete: HTMLElement; recognized: HTMLElement } {
        if (this.activeNameInput === 'zh') {
            return {
                input: document.getElementById('nameZh') as HTMLInputElement,
                autocomplete: this.fastFillAutocompleteZh,
                recognized: this.fastFillRecognizedZh
            };
        }
        return {
            input: document.getElementById('nameEn') as HTMLInputElement,
            autocomplete: this.fastFillAutocomplete,
            recognized: this.fastFillRecognized
        };
    }

    private async handleFastFillAutocomplete(): Promise<void> {
        const { input, autocomplete, recognized } = this.getActiveAutocompleteElements();
        const text = input?.value.trim() ?? '';
        const storedLocation = getStoredLocation();

        if (text.length < 2) {
            autocomplete?.classList.add('hidden');
            if (recognized) {
                recognized.textContent = '';
                recognized.className = 'fast-fill-recognized';
            }
            this.selectedFastFillPlaceId = null;
            return;
        }

        const requestId = ++this.fastFillAutocompleteRequestId;
        if (recognized) {
            recognized.textContent = i18n.searching;
            recognized.className = 'fast-fill-recognized resolving';
        }

        try {
            const response = await fetch('api/restaurants/fast-fill-autocomplete', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    input: text,
                    country_code: getPreferredCountryCode(),
                    language_code: getPlacesLanguageCode(),
                    location_lat: storedLocation?.lat ?? null,
                    location_lng: storedLocation?.lng ?? null
                }),
            });

            if (requestId !== this.fastFillAutocompleteRequestId) return;

            if (!response.ok) {
                autocomplete?.classList.add('hidden');
                if (recognized) {
                    recognized.textContent = '';
                    recognized.className = 'fast-fill-recognized';
                }
                return;
            }

            const data = await response.json();
            if (requestId !== this.fastFillAutocompleteRequestId) return;

            if (data.predictions && data.predictions.length > 0) {
                if (autocomplete) {
                    autocomplete.innerHTML = '';
                    autocomplete.classList.remove('hidden');

                    data.predictions.forEach((prediction: { name: string; address: string; place_id: string }) => {
                        const item = document.createElement('div');
                        item.className = 'fast-fill-autocomplete-item';
                        item.innerHTML = `
                            <div class="fast-fill-autocomplete-item-name">${prediction.name}</div>
                            <div class="fast-fill-autocomplete-item-address">${prediction.address}</div>
                        `;
                        item.addEventListener('click', () => {
                            this.selectFastFillRestaurant(prediction);
                        });
                        autocomplete.appendChild(item);
                    });
                }

                const first = data.predictions[0];
                if (recognized) {
                    recognized.textContent = `${i18n.recognized}: ${first.name} - ${first.address}`;
                    recognized.className = 'fast-fill-recognized recognized';
                }
                this.selectedFastFillPlaceId = first.place_id;
            } else {
                autocomplete?.classList.add('hidden');
                if (recognized) {
                    recognized.textContent = i18n.noMatchingRestaurants;
                    recognized.className = 'fast-fill-recognized';
                }
                this.selectedFastFillPlaceId = null;
            }
        } catch {
            if (requestId !== this.fastFillAutocompleteRequestId) return;
            autocomplete?.classList.add('hidden');
            if (recognized) {
                recognized.textContent = '';
                recognized.className = 'fast-fill-recognized';
            }
        }
    }

    private selectFastFillRestaurant(prediction: { name: string; address: string; place_id: string }): void {
        const { input, recognized } = this.getActiveAutocompleteElements();
        const addressInput = document.getElementById('address') as HTMLInputElement;
        const placeIdInput = document.getElementById('placeId') as HTMLInputElement;

        // Set name in the active input
        if (input) {
            input.value = prediction.name;
        }
        if (addressInput && prediction.address) {
            addressInput.value = prediction.address;
        }
        if (placeIdInput && prediction.place_id) {
            placeIdInput.value = prediction.place_id;
        }

        // Hide autocomplete for both fields
        this.fastFillAutocomplete.classList.add('hidden');
        this.fastFillAutocomplete.innerHTML = '';
        if (this.fastFillAutocompleteZh) {
            this.fastFillAutocompleteZh.classList.add('hidden');
            this.fastFillAutocompleteZh.innerHTML = '';
        }

        if (recognized) {
            recognized.textContent = `${i18n.selected}: ${prediction.name} - ${prediction.address}`;
            recognized.className = 'fast-fill-recognized recognized';
        }
        this.selectedFastFillPlaceId = prediction.place_id;

        input?.blur();

        if (this.editingRestaurantId === null) {
            this.checkAddressDuplicate();
        }
    }

    private async handleFastFill(): Promise<void> {
        const nameEnInput = document.getElementById('nameEn') as HTMLInputElement;
        const nameZhInput = document.getElementById('nameZh') as HTMLInputElement;
        // Use whichever name field has content
        const restaurantName = nameEnInput.value.trim() || nameZhInput.value.trim();

        if (!restaurantName) {
            alert(i18n.pleaseEnterName);
            return;
        }

        if (this.selectedFastFillPlaceId) {
            await this.fillFromPlaceId(this.selectedFastFillPlaceId);
            return;
        }

        const fastFillBtn = document.getElementById('fastFillBtn') as HTMLButtonElement;
        const originalText = fastFillBtn.textContent;
        const storedLocation = getStoredLocation();

        try {
            fastFillBtn.disabled = true;
            fastFillBtn.textContent = i18n.searching;

            const searchData = await this.api.fetch<{
                options: Array<{
                    place_id: string;
                    name: string;
                    address: string;
                    rating?: number;
                    user_ratings_total?: number;
                    price_level?: number;
                }>;
            }>('api/restaurants/fast-fill-search', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    restaurant_name: restaurantName,
                    country_code: getPreferredCountryCode(),
                    language_code: getPlacesLanguageCode(),
                    location_lat: storedLocation?.lat ?? null,
                    location_lng: storedLocation?.lng ?? null
                }),
            });

            if (!searchData.options || searchData.options.length === 0) {
                throw new Error(i18n.noRestaurantsFound);
            }

            const selectedOption = await this.showFastFillConfirmation(searchData.options);

            if (!selectedOption) {
                fastFillBtn.textContent = originalText;
                fastFillBtn.disabled = false;
                return;
            }

            await this.fillFromPlaceId(selectedOption.place_id);

        } catch (error) {
            console.error('Error in fast fill:', error);
            const errorDetail = error instanceof Error ? error.message : 'Unknown error';
            alert(`${i18n.autoFillFailed}: ${errorDetail}`);
            const fastFillBtn = document.getElementById('fastFillBtn') as HTMLButtonElement;
            fastFillBtn.textContent = originalText || '✨ Fast Fill';
            fastFillBtn.disabled = false;
        }
    }

    private async fillFromPlaceId(placeId: string): Promise<void> {
        const fastFillBtn = document.getElementById('fastFillBtn') as HTMLButtonElement;
        const originalText = fastFillBtn.textContent;

        try {
            fastFillBtn.disabled = true;
            fastFillBtn.textContent = i18n.fetchingDetails;

            const data = await this.api.fetch<{
                name_en?: string;
                name_zh?: string;
                address?: string;
                cuisine?: string;
                summary?: string;
                price_level?: number;
                budget_category?: string;
                top_dishes?: string[];
                keywords?: string[];
                opening_hours?: string;
            }>('api/restaurants/fast-fill', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    place_id: placeId,
                    language_code: getPlacesLanguageCode(),
                }),
            });

            if (data.name_en) {
                (document.getElementById('nameEn') as HTMLInputElement).value = data.name_en;
            }
            if (data.name_zh) {
                (document.getElementById('nameZh') as HTMLInputElement).value = data.name_zh;
            }
            if (data.address) {
                (document.getElementById('address') as HTMLInputElement).value = data.address;
            }
            (document.getElementById('placeId') as HTMLInputElement).value = placeId;
            if (data.cuisine) {
                (document.getElementById('cuisine') as HTMLInputElement).value = data.cuisine;
            }
            if (data.summary) {
                (document.getElementById('summary') as HTMLTextAreaElement).value = data.summary;
            }
            if (data.price_level) {
                (document.getElementById('priceLevel') as HTMLSelectElement).value = String(data.price_level);
            }
            if (data.budget_category) {
                (document.getElementById('budgetCategory') as HTMLSelectElement).value = data.budget_category;
            }
            if (data.top_dishes && data.top_dishes.length > 0) {
                (document.getElementById('topDishes') as HTMLInputElement).value = data.top_dishes.join(', ');
            }
            if (data.keywords && data.keywords.length > 0) {
                (document.getElementById('keywords') as HTMLInputElement).value = data.keywords.join(', ');
            }
            if (data.opening_hours) {
                (document.getElementById('openingHours') as HTMLTextAreaElement).value = data.opening_hours;
            }

            if (this.editingRestaurantId === null) {
                await this.checkAddressDuplicate();
            }

            fastFillBtn.textContent = `✓ ${i18n.autoFillSuccess}`;
            setTimeout(() => {
                fastFillBtn.textContent = originalText;
            }, 2000);

        } catch (error) {
            console.error('Error in fast fill:', error);
            const errorDetail = error instanceof Error ? error.message : 'Unknown error';
            alert(`${i18n.autoFillFailed}: ${errorDetail}`);
            fastFillBtn.textContent = originalText;
        } finally {
            fastFillBtn.disabled = false;
        }
    }

    private showFastFillConfirmation(options: Array<{
        place_id: string;
        name: string;
        address: string;
        rating?: number;
        user_ratings_total?: number;
        price_level?: number;
    }>): Promise<{ place_id: string } | null> {
        return new Promise((resolve) => {
            const dialog = document.createElement('div');
            dialog.className = 'fast-fill-dialog';
            let selectedOption: { place_id: string } | null = null;

            dialog.innerHTML = `
                <div class="fast-fill-dialog-content">
                    <div class="fast-fill-dialog-title">
                        ${i18n.selectRestaurant}
                    </div>
                    <div class="fast-fill-options">
                        ${options.map((option, index) => `
                            <div class="fast-fill-option ${index === 0 ? 'selected' : ''}" data-index="${index}">
                                <div class="fast-fill-option-name">${option.name}</div>
                                <div class="fast-fill-option-address">${option.address}</div>
                                <div class="fast-fill-option-meta">
                                    ${option.rating ? `<span>⭐ ${option.rating.toFixed(1)}</span>` : ''}
                                    ${option.user_ratings_total ? `<span>(${option.user_ratings_total} reviews)</span>` : ''}
                                    ${option.price_level ? `<span>${'$'.repeat(option.price_level)}</span>` : ''}
                                </div>
                            </div>
                        `).join('')}
                    </div>
                    <div class="fast-fill-dialog-actions">
                        <button class="btn-secondary" id="fastFillCancel">${i18n.cancel}</button>
                        <button class="btn-fast-fill" id="fastFillConfirm">${i18n.confirm}</button>
                    </div>
                </div>
            `;

            document.body.appendChild(dialog);
            selectedOption = options[0];

            dialog.querySelectorAll('.fast-fill-option').forEach((optionEl) => {
                optionEl.addEventListener('click', () => {
                    dialog.querySelectorAll('.fast-fill-option').forEach(el => el.classList.remove('selected'));
                    optionEl.classList.add('selected');
                    const index = parseInt((optionEl as HTMLElement).dataset.index || '0');
                    selectedOption = options[index];
                });
            });

            dialog.querySelector('#fastFillConfirm')?.addEventListener('click', () => {
                document.body.removeChild(dialog);
                resolve(selectedOption);
            });

            dialog.querySelector('#fastFillCancel')?.addEventListener('click', () => {
                document.body.removeChild(dialog);
                resolve(null);
            });
        });
    }

    // Magic Import
    showMagicImportModal(onImportComplete: () => Promise<void>): void {
        const dialog = document.createElement('div');
        dialog.className = 'magic-import-modal';
        dialog.innerHTML = `
            <div class="magic-import-dialog-content">
                <h3>🐙 ${i18n.magicImportTitle}</h3>

                <div class="magic-import-step" id="importStep1">
                    <div class="magic-import-help-box">
                        <div class="magic-import-help-header">
                            <span class="magic-import-help-icon">?</span>
                            <span class="magic-import-help-title">${i18n.magicImportHelpTitle}</span>
                        </div>
                        <p class="magic-import-help-text">${i18n.magicImportHelpText}</p>
                        <div class="magic-import-takeout-tip">
                            <strong>${i18n.magicImportTakeoutTip}</strong>
                            <p>${i18n.magicImportTakeoutLink}</p>
                            <a href="https://takeout.google.com/" target="_blank" rel="noopener noreferrer" class="magic-import-takeout-link">takeout.google.com</a>
                        </div>
                    </div>
                    <div class="form-group">
                        <label>${i18n.magicImportTextLabel}</label>
                        <textarea id="magicImportText" class="magic-import-textarea" rows="4" placeholder="${i18n.magicImportTextPlaceholder}"></textarea>
                    </div>
                    <div class="form-group">
                        <label>${i18n.magicImportFileLabel}</label>
                        <input type="file" id="magicImportFile" class="magic-import-file-input">
                    </div>
                    <div class="form-group">
                        <label>${i18n.magicImportTagLabel}</label>
                        <input type="text" id="magicImportTag" placeholder="${i18n.magicImportTagPlaceholder}" class="magic-import-input">
                    </div>
                    <div class="form-group">
                        <label>${i18n.magicImportLocationLabel}</label>
                        <input type="text" id="magicImportLocation" placeholder="${i18n.magicImportLocationPlaceholder}" class="magic-import-input">
                    </div>
                </div>

                <div class="magic-import-step hidden" id="importStep2">
                    <h4>${i18n.magicImportPreviewTitle}</h4>
                    <div class="magic-import-preview-list" id="importPreviewList"></div>
                </div>

                <div class="magic-import-step hidden" id="importStep3">
                    <div class="magic-import-progress">
                        <div class="magic-import-progress-bar">
                            <div class="magic-import-progress-fill" id="importProgressFill"></div>
                        </div>
                        <div class="magic-import-progress-text" id="importProgressText">${i18n.magicImportProcessing}...</div>
                        <div class="magic-import-status-legend">
                            <span class="legend-title">${i18n.magicImportStatusLegend}</span>
                            <span class="legend-item"><span class="legend-icon">⏳</span> ${i18n.magicImportStatusPending}</span>
                            <span class="legend-item"><span class="legend-icon">🔄</span> ${i18n.magicImportStatusProcessing}</span>
                            <span class="legend-item"><span class="legend-icon">✅</span> ${i18n.magicImportStatusSuccess}</span>
                            <span class="legend-item"><span class="legend-icon">📋</span> ${i18n.magicImportStatusDuplicate}</span>
                            <span class="legend-item"><span class="legend-icon">⚠️</span> ${i18n.magicImportStatusNotFound}</span>
                            <span class="legend-item"><span class="legend-icon">❌</span> ${i18n.magicImportStatusError}</span>
                        </div>
                        <div class="magic-import-progress-list" id="importProgressList"></div>
                    </div>
                </div>

                <div class="magic-import-step hidden" id="importStep4">
                    <h4>${i18n.magicImportComplete}</h4>
                    <div class="magic-import-results-summary" id="importResultsSummary"></div>
                </div>

                <div class="magic-import-actions">
                    <button class="btn-secondary" id="importCancelBtn">${i18n.cancel}</button>
                    <button class="btn-primary" id="importActionBtn">${i18n.magicImportExtract}</button>
                </div>
            </div>
        `;

        document.body.appendChild(dialog);

        let currentStep = 1;
        let extractedNames: string[] = [];
        let fileContent = '';

        const step1 = dialog.querySelector('#importStep1') as HTMLElement;
        const step2 = dialog.querySelector('#importStep2') as HTMLElement;
        const step3 = dialog.querySelector('#importStep3') as HTMLElement;
        const step4 = dialog.querySelector('#importStep4') as HTMLElement;
        const actionBtn = dialog.querySelector('#importActionBtn') as HTMLButtonElement;
        const cancelBtn = dialog.querySelector('#importCancelBtn') as HTMLButtonElement;
        const previewList = dialog.querySelector('#importPreviewList') as HTMLElement;
        const progressList = dialog.querySelector('#importProgressList') as HTMLElement;
        const progressFill = dialog.querySelector('#importProgressFill') as HTMLElement;
        const progressText = dialog.querySelector('#importProgressText') as HTMLElement;
        const resultsSummary = dialog.querySelector('#importResultsSummary') as HTMLElement;

        const textInput = dialog.querySelector('#magicImportText') as HTMLTextAreaElement;
        const fileInput = dialog.querySelector('#magicImportFile') as HTMLInputElement;

        // Clear text when file is selected
        fileInput.addEventListener('change', () => {
            const file = fileInput.files?.[0];
            if (file) {
                const reader = new FileReader();
                reader.onload = (e) => {
                    fileContent = e.target?.result as string;
                    // Clear text input when file is loaded
                    textInput.value = '';
                };
                reader.readAsText(file);
            }
        });

        // Clear file when text is typed
        textInput.addEventListener('input', () => {
            if (textInput.value.trim()) {
                fileInput.value = '';
                fileContent = '';
            }
        });

        cancelBtn.addEventListener('click', () => {
            document.body.removeChild(dialog);
        });

        actionBtn.addEventListener('click', async () => {
            if (currentStep === 1) {
                // Get content from text input or file
                const content = textInput.value.trim() || fileContent;
                if (!content) {
                    alert(i18n.magicImportNoInput);
                    return;
                }

                actionBtn.disabled = true;
                actionBtn.textContent = i18n.searching;

                try {
                    const response = await this.api.fetch<{ restaurant_names: string[] }>(
                        'api/restaurants/magic-import/extract-names',
                        {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ content: content })
                        }
                    );

                    extractedNames = response.restaurant_names;

                    previewList.innerHTML = extractedNames.map((name, i) => `
                        <label class="magic-import-preview-item">
                            <input type="checkbox" checked data-index="${i}">
                            <span>${name}</span>
                        </label>
                    `).join('');

                    step1.classList.add('hidden');
                    step2.classList.remove('hidden');
                    actionBtn.textContent = i18n.magicImportStartImport;
                    currentStep = 2;
                } catch (error) {
                    console.error('Failed to extract names:', error);
                    alert('Failed to extract restaurant names from content');
                } finally {
                    actionBtn.disabled = false;
                }
            } else if (currentStep === 2) {
                const checkboxes = previewList.querySelectorAll('input[type="checkbox"]:checked');
                const selectedNames = Array.from(checkboxes).map(cb => {
                    const index = parseInt((cb as HTMLInputElement).dataset.index || '0');
                    return extractedNames[index];
                });

                if (selectedNames.length === 0) {
                    alert('Please select at least one restaurant');
                    return;
                }

                const tagInput = dialog.querySelector('#magicImportTag') as HTMLInputElement;
                const locationInput = dialog.querySelector('#magicImportLocation') as HTMLInputElement;
                const tagNames = tagInput.value.trim()
                    ? tagInput.value.split(',').map(t => t.trim()).filter(t => t)
                    : [];
                const preferredLocation = locationInput.value.trim();

                step2.classList.add('hidden');
                step3.classList.remove('hidden');
                actionBtn.classList.add('hidden');
                currentStep = 3;

                progressList.innerHTML = selectedNames.map((name, i) => `
                    <div class="progress-item" id="progressItem${i}">
                        <span class="progress-icon">⏳</span>
                        <span class="progress-name">${escapeHtml(name)}</span>
                    </div>
                `).join('');

                let created = 0, duplicates = 0, notFound = 0, errors = 0;
                const storedLocation = getStoredLocation();

                for (let i = 0; i < selectedNames.length; i++) {
                    const name = selectedNames[i];
                    const itemEl = progressList.querySelector(`#progressItem${i}`) as HTMLElement;
                    const iconEl = itemEl.querySelector('.progress-icon') as HTMLElement;

                    iconEl.textContent = '🔄';
                    itemEl.classList.add('processing');

                    progressFill.style.width = `${((i + 1) / selectedNames.length) * 100}%`;
                    progressText.textContent = `${i18n.magicImportProcessing} ${i + 1}/${selectedNames.length}`;

                    try {
                        const result = await this.api.fetch<{ status: string }>(
                            'api/restaurants/magic-import/single',
                            {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({
                                    restaurant_name: name,
                                    tag_names: tagNames,
                                    preferred_location: preferredLocation,
                                    country_code: getPreferredCountryCode(),
                                    language_code: getPlacesLanguageCode(),
                                    location_lat: storedLocation?.lat ?? null,
                                    location_lng: storedLocation?.lng ?? null
                                })
                            }
                        );

                        itemEl.classList.remove('processing');

                        if (result.status === 'created') {
                            iconEl.textContent = '✅';
                            created++;
                        } else if (result.status === 'duplicate') {
                            iconEl.textContent = '📋';
                            duplicates++;
                        } else if (result.status === 'not_found') {
                            iconEl.textContent = '⚠️';
                            notFound++;
                        } else {
                            iconEl.textContent = '❌';
                            errors++;
                        }
                    } catch {
                        itemEl.classList.remove('processing');
                        iconEl.textContent = '❌';
                        errors++;
                    }
                }

                step3.classList.add('hidden');
                step4.classList.remove('hidden');
                cancelBtn.textContent = i18n.magicImportDone;
                currentStep = 4;

                resultsSummary.innerHTML = `
                    <div class="result-row">
                        <span class="result-badge result-created">✅ ${created} ${i18n.magicImportCreated}</span>
                        <span class="result-desc">${i18n.magicImportResultCreatedDesc}</span>
                    </div>
                    ${duplicates > 0 ? `<div class="result-row">
                        <span class="result-badge result-duplicate">📋 ${duplicates} ${i18n.magicImportDuplicate}</span>
                        <span class="result-desc">${i18n.magicImportResultDuplicateDesc}</span>
                    </div>` : ''}
                    ${notFound > 0 ? `<div class="result-row">
                        <span class="result-badge result-not-found">⚠️ ${notFound} ${i18n.magicImportNotFound}</span>
                        <span class="result-desc">${i18n.magicImportResultNotFoundDesc}</span>
                    </div>` : ''}
                    ${errors > 0 ? `<div class="result-row">
                        <span class="result-badge result-error">❌ ${errors} ${i18n.magicImportError}</span>
                        <span class="result-desc">${i18n.magicImportResultErrorDesc}</span>
                    </div>` : ''}
                `;

                await onImportComplete();
            }
        });
    }

    // Tag input methods
    setAvailableTags(tags: string[]): void {
        this.availableTags = tags;
    }

    private setupTagInputListeners(): void {
        if (!this.tagInputElement) return;

        this.tagInputElement.addEventListener('input', () => this.handleTagInput());
        this.tagInputElement.addEventListener('keydown', (e) => this.handleTagKeydown(e));
        this.tagInputElement.addEventListener('focus', () => this.showTagSuggestions());
        this.tagInputElement.addEventListener('blur', () => {
            // Delay hiding to allow click on suggestion
            setTimeout(() => this.hideTagSuggestions(), 150);
        });

        // Click on container focuses input
        const container = document.getElementById('tagInputContainer');
        if (container) {
            container.addEventListener('click', () => this.tagInputElement?.focus());
        }
    }

    private handleTagInput(): void {
        this.selectedTagSuggestionIndex = -1;
        this.showTagSuggestions();
    }

    private handleTagKeydown(e: KeyboardEvent): void {
        const suggestions = this.getVisibleSuggestions();

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            this.selectedTagSuggestionIndex = Math.min(
                this.selectedTagSuggestionIndex + 1,
                suggestions.length - 1
            );
            this.updateTagSuggestionSelection();
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            this.selectedTagSuggestionIndex = Math.max(this.selectedTagSuggestionIndex - 1, -1);
            this.updateTagSuggestionSelection();
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (this.selectedTagSuggestionIndex >= 0 && suggestions[this.selectedTagSuggestionIndex]) {
                this.addTag(suggestions[this.selectedTagSuggestionIndex]);
            } else if (this.tagInputElement?.value.trim()) {
                this.addTag(this.tagInputElement.value.trim());
            }
        } else if (e.key === 'Backspace' && !this.tagInputElement?.value) {
            // Remove last tag when backspace on empty input
            if (this.selectedTags.length > 0) {
                this.removeTag(this.selectedTags[this.selectedTags.length - 1]);
            }
        } else if (e.key === 'Escape') {
            this.hideTagSuggestions();
        }
    }

    private getVisibleSuggestions(): string[] {
        const query = this.tagInputElement?.value.toLowerCase().trim() || '';
        const suggestions: string[] = [];

        // Filter existing tags that match and aren't already selected
        for (const tag of this.availableTags) {
            if (!this.selectedTags.includes(tag)) {
                if (!query || tag.toLowerCase().includes(query)) {
                    suggestions.push(tag);
                }
            }
        }

        // If query doesn't match any existing tag exactly, add option to create new
        if (query && !this.availableTags.some(t => t.toLowerCase() === query) &&
            !this.selectedTags.some(t => t.toLowerCase() === query)) {
            suggestions.push(query);
        }

        return suggestions;
    }

    private showTagSuggestions(): void {
        if (!this.tagSuggestionsContainer) return;

        const suggestions = this.getVisibleSuggestions();

        if (suggestions.length === 0) {
            this.hideTagSuggestions();
            return;
        }

        this.tagSuggestionsContainer.innerHTML = '';

        for (let index = 0; index < suggestions.length; index++) {
            const tag = suggestions[index];
            const div = document.createElement('div');
            div.className = 'tag-suggestion';
            if (index === this.selectedTagSuggestionIndex) {
                div.classList.add('selected');
            }

            // Check if this is a new tag (not in availableTags)
            const isNew = !this.availableTags.includes(tag);
            if (isNew) {
                div.classList.add('tag-suggestion-new');
            }

            div.textContent = tag;
            div.addEventListener('mousedown', (e) => {
                e.preventDefault(); // Prevent blur
                this.addTag(tag);
            });
            this.tagSuggestionsContainer!.appendChild(div);
        }

        this.tagSuggestionsContainer.classList.remove('hidden');
    }

    private hideTagSuggestions(): void {
        if (this.tagSuggestionsContainer) {
            this.tagSuggestionsContainer.classList.add('hidden');
        }
        this.selectedTagSuggestionIndex = -1;
    }

    private updateTagSuggestionSelection(): void {
        if (!this.tagSuggestionsContainer) return;

        const items = this.tagSuggestionsContainer.querySelectorAll('.tag-suggestion');
        items.forEach((item, index) => {
            if (index === this.selectedTagSuggestionIndex) {
                item.classList.add('selected');
                item.scrollIntoView({ block: 'nearest' });
            } else {
                item.classList.remove('selected');
            }
        });
    }

    private addTag(tag: string): void {
        const normalizedTag = tag.trim();
        if (!normalizedTag || this.selectedTags.includes(normalizedTag)) return;

        this.selectedTags.push(normalizedTag);
        this.renderTagChips();

        if (this.tagInputElement) {
            this.tagInputElement.value = '';
        }
        this.selectedTagSuggestionIndex = -1;
        this.showTagSuggestions(); // Refresh suggestions
    }

    private removeTag(tag: string): void {
        const index = this.selectedTags.indexOf(tag);
        if (index > -1) {
            this.selectedTags.splice(index, 1);
            this.renderTagChips();
        }
    }

    private renderTagChips(): void {
        if (!this.tagChipsContainer) return;

        this.tagChipsContainer.innerHTML = '';

        for (const tag of this.selectedTags) {
            const chip = document.createElement('span');
            chip.className = 'tag-chip';
            chip.innerHTML = `
                ${escapeHtml(tag)}
                <button type="button" class="tag-chip-remove" data-tag="${escapeHtml(tag)}">&times;</button>
            `;

            const removeBtn = chip.querySelector('.tag-chip-remove');
            if (removeBtn) {
                removeBtn.addEventListener('click', (e) => {
                    e.stopPropagation();
                    this.removeTag(tag);
                });
            }

            this.tagChipsContainer.appendChild(chip);
        }
    }
}
